/**
 * Message composer — textarea + DE picker chip + file dropzone + send.
 *
 * For AIAccountant we expect drag-drop of PDFs/Excel which routes the message
 * to /tally-import via the DE picker. We pass attachments through to the
 * caller; uploading is the caller's responsibility (so the parent can attach
 * uploads to the conversation context).
 */

import { useRef, useState, useEffect } from 'react';
import { Send, Paperclip, X, Loader2, Square } from 'lucide-react';
import clsx from 'clsx';
import type { DigitalEmployee } from '@/types/chat';

export interface ComposerSubmit {
  message: string;
  files: File[];
  slash?: string; // e.g. '/tally-import' if user explicitly picked a DE
}

interface ComposerProps {
  onSubmit: (s: ComposerSubmit) => void;
  des?: DigitalEmployee[];
  busy?: boolean;
  defaultSlash?: string; // default DE for this session
  /** Cancel an in-flight stream/job. Shown only while busy. */
  onStop?: () => void;
}

export function Composer({ onSubmit, des, busy, defaultSlash, onStop }: ComposerProps) {
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [slash, setSlash] = useState<string | undefined>(defaultSlash);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 200) + 'px';
  }, [text]);

  const canSend = (text.trim().length > 0 || files.length > 0) && !busy;

  const send = () => {
    if (!canSend) return;
    onSubmit({ message: text.trim(), files, slash });
    setText('');
    setFiles([]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files.length) {
      setFiles((prev) => [...prev, ...Array.from(e.dataTransfer.files)]);
    }
  };

  return (
    <div className="border-t border-border p-3 bg-background">
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {files.map((f, i) => (
            <FileChip
              key={i}
              file={f}
              onRemove={() => setFiles(files.filter((_, j) => j !== i))}
            />
          ))}
        </div>
      )}

      <div
        className="flex items-end gap-2 border border-border rounded-lg px-3 py-2 bg-card focus-within:ring-2 focus-within:ring-ring transition-shadow"
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
      >
        <button
          onClick={() => fileRef.current?.click()}
          className="p-1.5 rounded hover:bg-muted text-muted-foreground"
          title="Attach file"
          aria-label="Attach file"
        >
          <Paperclip className="w-4 h-4" />
        </button>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept=".pdf,.xls,.xlsx,.csv,image/*"
          className="hidden"
          onChange={(e) => {
            if (e.target.files) {
              setFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
            }
          }}
        />

        {des && des.length > 0 && (
          <DEPicker des={des} value={slash} onChange={setSlash} />
        )}

        <textarea
          ref={taRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder={
            files.length > 0
              ? 'Add a note (e.g. "Import to https://...ngrok.app")…'
              : 'Type a message — drag a PDF to import to Tally…'
          }
          className="flex-1 resize-none bg-transparent outline-none text-sm py-1 max-h-[200px]"
        />

        {busy && onStop ? (
          <button
            onClick={onStop}
            className="p-2 rounded-md bg-destructive text-destructive-foreground hover:bg-destructive/90"
            title="Stop"
            aria-label="Stop"
          >
            <Square className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={send}
            disabled={!canSend}
            className={clsx(
              'p-2 rounded-md transition-colors',
              canSend
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : 'bg-muted text-muted-foreground cursor-not-allowed',
            )}
            title="Send (Enter)"
            aria-label="Send message"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        )}
      </div>

      <div className="text-[10px] text-muted-foreground mt-1.5 px-1">
        Enter to send · Shift+Enter for newline · Drag-drop PDF/Excel to import to Tally
      </div>
    </div>
  );
}

function FileChip({ file, onRemove }: { file: File; onRemove: () => void }) {
  const sizeKb = (file.size / 1024).toFixed(0);
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-muted">
      📎 {file.name} <span className="text-muted-foreground">({sizeKb} KB)</span>
      <button onClick={onRemove} className="ml-1 hover:bg-muted-foreground/20 rounded p-0.5">
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

function DEPicker({
  des,
  value,
  onChange,
}: {
  des: DigitalEmployee[];
  value: string | undefined;
  onChange: (slash: string | undefined) => void;
}) {
  // Augment the catalog with built-in AIAccountant options so users always
  // have explicit choices even if the chat-bll catalog hasn't seeded them.
  const augmented: DigitalEmployee[] = (() => {
    const slashes = new Set(des.map((d) => d.slash));
    const extras: DigitalEmployee[] = [];
    if (!slashes.has('/tally-import')) {
      extras.push({
        id: 'builtin-tally-import',
        slash: '/tally-import',
        de_name: 'PDF → Tally',
        de_action: 'import_bank_statement',
        active: true,
        display_name: 'AI Accountant — PDF/Excel to Tally',
        icon: '🧾',
      });
    }
    if (!slashes.has('/financial-report')) {
      extras.push({
        id: 'builtin-financial-report',
        slash: '/financial-report',
        de_name: 'Financial Report',
        de_action: 'generate_report',
        active: true,
        display_name: 'AI Accountant — Financial Report',
        icon: '💰',
      });
    }
    return [...extras, ...des];
  })();

  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || undefined)}
      className="text-xs bg-muted rounded px-2 py-1 outline-none border-none cursor-pointer max-w-[260px]"
      title="Choose how to handle this message"
    >
      <option value="">Auto (chat with Secretary)</option>
      <optgroup label="AI Accountant">
        {augmented
          .filter((de) => de.slash === '/tally-import' || de.slash === '/financial-report')
          .map((de) => (
            <option key={de.id ?? de.slash} value={de.slash}>
              {de.icon} {de.display_name}
            </option>
          ))}
      </optgroup>
      <optgroup label="Other Digital Employees">
        {augmented
          .filter((de) => de.slash !== '/tally-import' && de.slash !== '/financial-report')
          .map((de) => (
            <option key={de.id ?? de.slash} value={de.slash}>
              {de.icon} {de.display_name}
            </option>
          ))}
      </optgroup>
    </select>
  );
}
